/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as R from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { StatusType, User } from '@weblite/ts-action/user'
import { Contact } from '@weblite/ts-action/contact'
import { RootState } from '$setup/redux'
import { usersStorage } from '$logic/users/storage'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { useClientUser } from '$logic/client-user/react-hook'
import {
  DEFAULT_OPEN_LIST_TYPE,
  OpenedUserListItem,
  useOpenedUserListAsyncIntegration,
} from '$logic/opened-user-list'
import { AddMemberItem } from '$comp/_app/components/leftPanel/components/add-members'
import { checkHallSubscribersList } from '$logic/subscription'
import { invalidatedUserStatus } from '$logic/users/user-status.storage'
import { fetchUserStatus } from '$logic/users/user-status.service'
import { getClientUser, getClientUserId } from '$logic/client-user'
import { useFuse } from '$func/hooks'
import { getFamiliarUserIds, getUser, getUserByPhone } from './service'
import { addCountryCodeToNumber } from '$func/phone'

const getUsersView = (state: RootState) => state.main.users as User[]

const getContactsView = (state: RootState) => state.main.contacts as Contact[]

const getContactView = (userId: string) => (state: RootState) =>
  state.main.contacts[userId]

const getUserStatusView = (userId: string) => (state: RootState) =>
  state.main.usersStatus[userId]?.status as StatusType

/**
 * User
 * 1. resolved from redux
 * 2. resolved from indexeddb/server
 * 3. not exists
 *
 * Need:
 * 1. show loading
 * 2. cancel loading (error, object)
 * 3. resolve data
 * 4. sensitive on data
 */
export function useUser(userId: string = '') {
  const user: User = useSelector(() => usersStorage.getFromRedux(userId))
  const contact = useSelector(getContactView(userId)) ?? {}
  const [err, setErr] = useState(null)
  const clientUser = useClientUser()

  useEffect(() => {
    if (user || clientUser?.id === userId || !userId) return

    getUser(userId).catch(setErr)
  }, [user, userId, clientUser?.id])

  if (!userId) return { err: new Error('userId is not valid') }

  if (clientUser?.id === userId)
    return { user: { ...(clientUser as User), isContact: false } }
  // TODO: Think about merging logic in react component
  if (err) return { err }
  if (!user) return { loading: true }

  const isContact = !!contact?.phone
  return {
    user: {
      ...user,
      ...(isContact && R.pick(['phone', 'firstname', 'lastname'], contact)),
      isContact,
    }, // Note: When we return object it has unique  reference everytime
  }
}

export function useUserStatus(id: string = '') {
  const status = useSelector(getUserStatusView(id))
  useEffect(() => {
    if (!status && invalidatedUserStatus.has(id)) {
      invalidatedUserStatus.delete(id)
      fetchUserStatus(id)
    }
  }, [status, id])
  return id === getClientUserId() ? 'ONLINE' : status
}

export function useNotSubscribedFamiliarUserIds(
  roomId: string,
  key = DEFAULT_OPEN_LIST_TYPE,
) {
  const { loading: familiarIdsLoading, items: familiarUserIds } =
    useFamiliarUserIds(key)

  const { loading: checkSubscriptionLoading, value: subscribers } =
    useAsyncFunction(
      () => checkHallSubscribersList(roomId, R.pluck('id', familiarUserIds)),
      [roomId, familiarUserIds],
    )

  const loading = familiarIdsLoading || checkSubscriptionLoading
  return {
    loading,
    items: useMemo(
      () =>
        loading
          ? []
          : familiarUserIds.map(
              ({ id }) =>
                ({
                  disabled: subscribers?.includes(id),
                  id,
                }) as AddMemberItem,
            ),
      [loading, familiarUserIds, subscribers],
    ),
  }
}

export function useFamiliarUserIds(key = 'common') {
  return useOpenedUserListAsyncIntegration(
    async () => {
      const userIds = await getFamiliarUserIds()

      const validUsersIds = await Promise.all(
        userIds.map(async id => {
          const user = await usersStorage.get(id)
          return user && !user.deletedAt ? id : null
        }),
      )
      return validUsersIds
        .filter(Boolean)
        .map(id => ({ id })) as OpenedUserListItem[]
    },
    [],
    key,
  )
}

export function useSearchedUserByIds({
  query,
  userIds,
  searchThreshold,
}: {
  query: string
  userIds: string[]
  searchThreshold?: number
}) {
  const users = useSelector(getUsersView)
  const contacts = useSelector(getContactsView)

  // NOTE: We should call usersStorage.get to make sure that all users will be loaded into redux.
  const { loading } = useAsyncFunction(
    () => Promise.all(userIds.map(id => usersStorage.get(id))),
    [userIds.join('')],
  )

  const usersContents = useMemo(() => {
    return userIds.map(id => {
      const { firstname: contactFirstname, lastname: contactLastname } =
        contacts[id] || {}
      const {
        firstname: userFirstname,
        lastname: userLastname,
        username,
      } = users[id] || {}
      return {
        contactFullName: `${contactFirstname || ''} ${contactLastname || ''}`,
        userFullName: `${userFirstname || ''} ${userLastname || ''}`,
        username,
        id,
      }
    })
  }, [userIds, contacts, users])

  const results = useFuse({
    items: usersContents,
    keys: ['contactFullName', 'userFullName', 'username'],
    query,
    limit: undefined,
    threshold: searchThreshold,
  })

  const searchedItems = useMemo(() => results.map(({ id }) => id), [results])

  return { loading, searchedItems }
}

export function useSearchedNonMemberFamiliarUserIds(params: {
  query: string
  items: AddMemberItem[]
}) {
  const { query, items } = params || { query: '', items: [] }
  const { loading, searchedItems } = useSearchedUserByIds({
    query,
    userIds: items.map(({ id }) => id),
  })
  return {
    loading,
    searchedItems: useMemo(
      () =>
        loading
          ? []
          : searchedItems.map(id => items.find(item => item.id === id)),
      [items, searchedItems, loading],
    ),
  }
}

export function useSearchedFamiliarUserIds({
  query,
  items,
}: {
  query: string
  items: { id: string }[]
}) {
  const { loading, searchedItems } = useSearchedUserByIds(
    query,
    items.map(u => u.id),
  )
  return { loading, searchedItems: searchedItems.map(id => ({ id })) }
}

export function useUserByPhone(phone: string) {
  const { value: user } = useAsyncFunction(async () => {
    const formattedPhone = phone.startsWith('+')
      ? phone
      : addCountryCodeToNumber(phone, '98')
    const clientUser = getClientUser()

    if (clientUser.phone === formattedPhone)
      return { user: clientUser, isClient: true }

    const user = await getUserByPhone(formattedPhone)
    return { user, isClient: false, deleted: !!user?.deletedAt }
  }, [phone])

  return user
}
